<template>
  <v-card id="acceptance-depollution" class="pa-4 mb-4">
    <div class="d-flex flex-wrap align-center ga-2 mb-3">
      <span class="text-subtitle-1 font-weight-medium mr-1">
        {{ $t("acceptance.depollution.title") }}
      </span>
      <v-chip v-if="chip" size="small" variant="tonal" :color="chip.color">
        {{ $t(chip.key) }}
      </v-chip>
      <span v-if="version" class="text-caption text-grey-darken-1">
        {{ $t("acceptance.depollution.template_version", { version }) }}
      </span>
      <v-chip
        v-if="deadline"
        size="small"
        variant="tonal"
        :color="deadline.color"
        class="ml-auto"
      >
        {{ $t(deadline.key, deadline.params) }}
      </v-chip>
    </div>

    <v-skeleton-loader v-if="state === 'loading'" type="text, text" />

    <div
      v-else-if="state === 'no_checklist'"
      class="text-body-2 text-grey-darken-1"
    >
      {{ $t("acceptance.depollution.not_opened") }}
    </div>

    <v-alert
      v-else-if="state === 'error'"
      type="warning"
      variant="tonal"
      density="compact"
    >
      <div class="d-flex flex-wrap align-center">
        <span class="mr-auto py-1">
          {{ $t("acceptance.depollution.unavailable") }}
        </span>
        <v-btn
          size="small"
          variant="text"
          color="primary"
          @click="$emit('reload')"
        >
          {{ $t("acceptance.conflict.reload") }}
        </v-btn>
      </div>
    </v-alert>

    <template v-else-if="view">
      <!-- Someone else changed the checklist (409): only the checklist reloads. -->
      <v-alert
        v-if="conflict"
        type="info"
        variant="tonal"
        density="compact"
        class="mb-3"
      >
        <div class="d-flex flex-wrap align-center">
          <div class="mr-auto py-1">
            <strong>{{ $t("acceptance.conflict.title") }}</strong>
          </div>
          <v-btn color="primary" variant="flat" @click="$emit('reload')">
            {{ $t("acceptance.conflict.reload") }}
          </v-btn>
        </div>
      </v-alert>

      <!-- Quarantine never blocks the depollution: a warning only. -->
      <v-alert
        v-if="quarantine"
        type="warning"
        variant="tonal"
        density="compact"
        class="mb-3"
      >
        {{ $t("acceptance.depollution.quarantine") }}
      </v-alert>

      <div class="d-flex align-center ga-3 mb-1">
        <v-progress-linear
          :model-value="count.percent"
          height="8"
          rounded
          :color="view.state === 'approved' ? 'green' : 'primary'"
        />
        <span class="text-body-2 text-no-wrap">
          {{
            $t("acceptance.depollution.progress", {
              done: count.done,
              total: count.total,
            })
          }}
        </span>
      </div>
      <div
        v-if="view.state !== 'approved'"
        class="text-body-2 depollution-wrap"
      >
        <template v-if="count.currentTaskCode">
          {{
            $t("acceptance.depollution.current_task", {
              task: $t(taskKey(count.currentTaskCode)),
            })
          }}
          ·
        </template>
        {{ $t("acceptance.depollution.remaining", { count: count.remaining }) }}
      </div>

      <div v-if="exceptions.length" class="mt-3">
        <div class="text-body-2 font-weight-medium mb-1">
          {{
            $t("acceptance.depollution.pending_exceptions", {
              count: exceptions.length,
            })
          }}
        </div>
        <div v-for="row in exceptions" :key="row.resultId" class="mb-1">
          <div class="d-flex align-start text-body-2 depollution-wrap">
            <v-icon size="16" color="amber-darken-2" class="mr-1 mt-1">
              mdi-alert-outline
            </v-icon>
            <span class="mr-auto">
              <strong>{{ $t(taskKey(row.taskCode)) }}</strong>
              <template v-if="row.reason"> · {{ row.reason }}</template>
            </span>
            <!-- Slice 9: the reason is a recorded value; Manager/Admin correct it (canCorrect). -->
            <v-btn
              v-if="view.canCorrect && taskOf(row)"
              size="small"
              variant="tonal"
              color="primary"
              class="ml-2"
              :disabled="deciding"
              @click="$emit('correct', { task: taskOf(row), field: 'reason' })"
            >
              {{ $t("acceptance.corrections.correct") }}
            </v-btn>
            <!-- Manager/Admin (the view's canAcceptExceptions; the API checks again). -->
            <v-btn
              v-if="panel.canAccept"
              size="small"
              variant="tonal"
              color="green"
              class="ml-2"
              :disabled="deciding"
              @click="$emit('accept', row)"
            >
              {{ $t("acceptance.depollution.accept") }}
            </v-btn>
          </div>
          <div
            v-if="previousReason(row)"
            class="text-caption text-grey-darken-1 depollution-wrap ml-6"
          >
            {{ $t("workshop.correction.previous", previousReason(row)) }}
          </div>
        </div>
      </div>

      <div v-if="blocking" class="text-caption text-grey-darken-1 mt-2">
        {{ $t(blocking.key, blocking.params) }}
      </div>

      <v-alert
        v-if="view.approval"
        type="success"
        variant="tonal"
        density="compact"
        class="mt-3"
      >
        {{
          $t("acceptance.depollution.approved_line", {
            name:
              (view.approval.approvedBy && view.approval.approvedBy.name) || "",
            date: formatCivilDate(view.approval.approvedDateRome),
          })
        }}
      </v-alert>

      <!-- Stage 6 slice 4: the car-body release, only with waste on. -->
      <template v-if="wasteEnabled && panel.approved">
        <div v-if="releaseLineParams" class="mt-3">
          <div
            class="d-flex flex-wrap align-center ga-2 text-body-2 depollution-wrap"
          >
            <v-icon size="18" color="grey-darken-1">mdi-car-off</v-icon>
            <span class="mr-auto">
              {{ $t("acceptance.car_body.released_line", releaseLineParams) }}
            </span>
            <v-btn
              v-if="releaseButtons.canCancel"
              size="small"
              variant="tonal"
              color="red-darken-3"
              :disabled="releaseSaving"
              @click="$emit('cancel-release')"
            >
              {{ $t("acceptance.car_body.cancel") }}
            </v-btn>
          </div>
          <!-- D7: once the area is sent out the release stays; say why there is no cancel. -->
          <div
            v-if="releaseButtons.sentOut"
            class="text-caption text-grey-darken-1 depollution-wrap ml-7"
          >
            {{ $t("acceptance.car_body.sent_out_note") }}
          </div>
        </div>
        <div
          v-else-if="releaseState === 'error'"
          class="text-caption text-grey-darken-1 mt-2"
        >
          {{ $t("acceptance.car_body.unavailable") }}
        </div>
        <div
          v-else-if="releaseButtons.noArea"
          class="text-caption text-grey-darken-1 mt-2"
        >
          {{ $t("acceptance.car_body.no_area") }}
        </div>
      </template>

      <div class="d-flex flex-wrap justify-end ga-2 mt-3">
        <v-btn
          v-if="wasteEnabled && releaseButtons.canRelease"
          variant="flat"
          color="primary"
          prepend-icon="mdi-car-off"
          :disabled="releaseSaving"
          @click="$emit('release')"
        >
          {{ $t("acceptance.car_body.release") }}
        </v-btn>
        <v-btn
          v-if="panel.manager && !panel.approved"
          variant="flat"
          color="green"
          prepend-icon="mdi-check-all"
          :disabled="!panel.canApprove || deciding"
          :loading="deciding"
          @click="$emit('approve')"
        >
          {{ $t("workshop.checklist.approve") }}
        </v-btn>
        <v-btn
          variant="outlined"
          color="primary"
          prepend-icon="mdi-wrench-outline"
          :to="{
            name: 'workshop-checklist',
            params: { checklistId: view.checklist.id },
          }"
        >
          {{ $t("acceptance.depollution.open_workshop") }}
        </v-btn>
      </div>
    </template>
  </v-card>
</template>

<script>
import { formatCivilDate } from "@/components/Acceptance/deadlines.mjs";
import {
  approvalPanel,
  blockingText,
  counters,
  deadlineChip,
  hasWarning,
  pendingExceptionRows,
  stateChip,
  taskKey,
} from "@/components/Workshop/workshopView.mjs";
import {
  displayText,
  previousLines,
} from "@/components/Workshop/workshopCorrections.mjs";
import {
  releaseActions,
  releaseLine,
} from "@/components/Waste/carBodyRelease.mjs";

// The "Bonifica" card of a confirmed acceptance (Stage 3a plan §D3): the checklist state, template
// version, deadline, progress, the current task, the quarantine banner, the exceptions waiting
// for acceptance with "Accetta", "Approva bonifica" (disabled while something blocks) and "Apri in
// officina" (the task screen). Both decisions are Manager/Admin only (the view's flags; the API
// checks again). The page loads the view through its own checklist session, asks for confirmation
// and runs the writes (emit "accept" with the exception row, "approve"). Slice 9: a pending
// exception's reason has "Correggi" (emit "correct" with { task, field: "reason" }) and its
// "Valore precedente" line. Stage 6 slice 4 (waste on, after approval): "Rilascia carcassa"
// (the release view's canRelease; emit "release"), the line "Carcassa rilasciata il … · area · da
// … · N ricambi" and "Annulla rilascio" while the area is not sent out (emit "cancel-release").
export default {
  name: "AcceptanceDepollutionCard",
  props: {
    // "loading" | "no_checklist" | "error" | "checklist" (workshopView.cardState)
    state: { type: String, required: true },
    // GET /compliance/dossiers/:id/workshop-checklist, or null
    view: { type: Object, default: null },
    // The checklist session's 409 (its own revision, L20)
    conflict: { type: Boolean, default: false },
    // An acceptance or the approval is being saved
    deciding: { type: Boolean, default: false },
    // Stage 6 slice 4: waste on, the GET /car-body-release view (or null), its load state
    // ("loading" | "ok" | "error" | null) and whether a release or cancel is being saved.
    wasteEnabled: { type: Boolean, default: false },
    release: { type: Object, default: null },
    releaseState: { type: String, default: null },
    releaseSaving: { type: Boolean, default: false },
  },
  emits: [
    "reload",
    "accept",
    "approve",
    "correct",
    "release",
    "cancel-release",
  ],
  computed: {
    releaseLineParams() {
      return releaseLine(this.release);
    },
    releaseButtons() {
      return releaseActions(this.release);
    },
    panel() {
      return approvalPanel(this.view);
    },
    chip() {
      return this.view ? stateChip(this.view.state) : null;
    },
    version() {
      const template = this.view && this.view.checklist.template;
      return template ? template.version : null;
    },
    deadline() {
      return this.view ? deadlineChip(this.view.deadline) : null;
    },
    count() {
      return counters(this.view);
    },
    quarantine() {
      return hasWarning(this.view, "quarantine");
    },
    exceptions() {
      return pendingExceptionRows(this.view);
    },
    blocking() {
      return blockingText(this.view);
    },
  },
  methods: {
    taskKey,
    formatCivilDate,
    // The task of an exception row while the row's result is still current.
    taskOf(row) {
      const task = ((this.view && this.view.tasks) || []).find(
        (item) => item.code === row.taskCode,
      );
      return task && task.current && task.current.id === row.resultId
        ? task
        : null;
    },
    // { value, name, date } of the reason's latest correction, or null.
    previousReason(row) {
      const task = this.taskOf(row);
      const line = task
        ? previousLines(task.current, { locale: this.$i18n.locale }).find(
            (item) => item.field === "reason",
          )
        : null;
      if (!line) return null;
      return {
        value: displayText(line.display, (key) => this.$t(key)),
        name: line.name,
        date: line.date,
      };
    },
  },
};
</script>

<style scoped>
.depollution-wrap {
  overflow-wrap: anywhere;
}
</style>
